
import { ComputedRef, defineComponent } from "vue";
import { computed } from "@vue/reactivity";
import { useStore } from "vuex";
import { dispatchError } from "../../utils/error_handling";
import { useRouter } from "vue-router";
import { Content, ExerciseStudent } from "./helpers";
import TUIViewer from "../../components/application/TUIViewer.vue";
import {
  ContentProvider,
  ContentProviderType
} from "../../../../domain/entities/content-provider/content-provider";
import { formattedDuration } from "../../components/mixins";
import { formatTime } from "../exercise/exercise_helpers";
import TimeFormatter from "./TimeFormatter.vue";
import {
  reloadContent,
  reloadContentList
} from "./questionnaire_error_handler";

export default defineComponent({
  name: "QuestionnaireCover",
  components: { TUIViewer, TimeFormatter },

  setup() {
    const router = useRouter();
    const store = useStore();

    const currentContent: ComputedRef<Content> = computed(
      () => store.getters["content_module/currentContent"]
    );
    const exercise: ComputedRef<ExerciseStudent> = computed(
      () => store.getters["content_module/exercise/exercise"]
    );

    const onClickStart = async (): Promise<void> => {
      try {
        const questionnaireID = await generateQuestionnaire();
        await redirectToQuestionnaire(questionnaireID);
      } catch (e) {
        console.error("[onClickStart] Error ", e);
        await dispatchError(e);
      }
    };

    const generateQuestionnaire = async (): Promise<number> => {
      try {
        return await store.dispatch(
          "content_module/exercise/generateQuestionnaire"
        );
      } catch (e) {
        console.error("[loadQuestionnaire] Error ", e);
        throw e;
      }
    };

    const redirectToQuestionnaire = async (questionnaireID: number) => {
      try {
        const contentProvider = store.getters[
          "content_module/contentProvider"
        ] as ContentProvider;
        switch (contentProvider.getType()) {
          case ContentProviderType.COURSE:
            await router.push({
              name: "course_questionnaire",
              params: { questionnaire_id: questionnaireID, page: 1 }
            });
            break;
          case ContentProviderType.EVENT:
            await router.push({
              name: "event_questionnaire",
              params: { questionnaire_id: questionnaireID, page: 1 }
            });
            break;
        }
      } catch (e) {
        console.error("[redirectToQuestionnaire] Error ", e);
        throw e;
      }
    };

    const onClickContinue = async (): Promise<void> => {
      try {
        await redirectToQuestionnaire(exercise.value.status.questionnaire.id);
      } catch (e) {
        console.error("[onClickContinue] Error ", e);
        await dispatchError(e);
      }
    };

    const onClickRedo = async (): Promise<void> => {
      try {
        const questionnaireID = await generateQuestionnaire();
        await redirectToQuestionnaire(questionnaireID);
        await reloadContentList();
      } catch (e) {
        console.error("[onClickRedo] Error ", e);
        await dispatchError(e);
      }
    };

    const onClickFinish = async (): Promise<void> => {
      try {
        await finishExercise();
        await reloadContentList();
      } catch (e) {
        console.error("[onClickFinish] Error ", e);
        await dispatchError(e);
      }
    };

    const finishExercise = async (): Promise<void> => {
      try {
        return await store.dispatch(
          "content_module/exercise/finishQuestionnaire"
        );
      } catch (e) {
        console.error("[loadQuestionnaire] Error ", e);
        throw e;
      }
    };

    const onTimeExpired = async (): Promise<void> => {
      try {
        await finishExercise();
        await reloadContent();
        await reloadContentList();
      } catch (e) {
        console.error("[onTimeExpired] Error ", e);
        await dispatchError(e);
      }
    };

    const goToUserResponses = () => {
      const questionnaireId = exercise.value.status.questionnaire.id;

      router.push({
        name: "user_responses",
        params: {
          questionnaireId
        }
      });
    };

    return {
      currentContent,
      exercise,
      goToUserResponses,
      onClickStart,
      onClickContinue,
      onClickRedo,
      onClickFinish,
      formattedDuration,
      formatTime,
      onTimeExpired
    };
  }
});
